import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { fetchBffUserClaims, findClaim, login as bffLogin, logout as bffLogout, type BffUserClaim } from '../services/authService';

export interface AuthContextValue {
  isAuthChecked: boolean;
  isAuthenticated: boolean;
  claims: BffUserClaim[] | null;
  logoutUrl: string | null;
  login: (returnPath?: string) => void;
  logout: () => void;
  refreshSession: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const [claims, setClaims] = useState<BffUserClaim[] | null>(null);
  const claimsRef = useRef<BffUserClaim[] | null>(null);
  const inFlightSessionRef = useRef<Promise<boolean> | null>(null);
  const sessionLostRedirectIssuedRef = useRef(false);

  useEffect(() => {
    claimsRef.current = claims;
  }, [claims]);

  const refreshSession = useCallback((): Promise<boolean> => {
    if (inFlightSessionRef.current)
      return inFlightSessionRef.current;

    const hadClaimsBefore = claimsRef.current !== null;

    const run = (async (): Promise<boolean> => {
      const next = await fetchBffUserClaims();
      setIsAuthChecked(true);
      setClaims(next);
      claimsRef.current = next;
      if (next !== null) {
        sessionLostRedirectIssuedRef.current = false;
        return true;
      }
      if (hadClaimsBefore && !sessionLostRedirectIssuedRef.current) {
        sessionLostRedirectIssuedRef.current = true;
        bffLogin();
      }
      return false;
    })();

    const tracked = run.finally(() => {
      inFlightSessionRef.current = null;
    });
    inFlightSessionRef.current = tracked;
    return tracked;
  }, []);

  useEffect(() => {
    void refreshSession();
  }, [refreshSession]);

  const logoutUrl = useMemo(
    () => findClaim(claims, 'bff:logout_url') ?? null,
    [claims],
  );

  const isAuthenticated = claims !== null;

  const login = useCallback((returnPath?: string) => {
    bffLogin(returnPath);
  }, []);

  const logout = useCallback(() => {
    if (logoutUrl)
      bffLogout(logoutUrl);
    else
      globalThis.location.assign('/');
  }, [logoutUrl]);

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthChecked,
      isAuthenticated,
      claims,
      logoutUrl,
      login,
      logout,
      refreshSession,
    }),
    [isAuthChecked, isAuthenticated, claims, logoutUrl, login, logout, refreshSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx)
    throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
